import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestInsuredService } from '@services/requestInsured.service';
import { RequestService } from '@services/request.service';
import { EntityService } from '@services/entity.service';
import { PolicyService } from '@services/policy.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PanelComponent } from 'app/components/core-template/panel/panel.component';
import { CoreModule } from 'app/modules/core.module';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsCoreOption } from 'echarts';
import Swal from 'sweetalert2';
import { DashboardDetailModalComponent } from './dashboard-detail-modal.component';

type DetailType = 'coverage' | 'campus' | 'turning65' | 'turning65During' | 'cargos'

const EXPORT_API_TYPE: Record<string, string> = {
  turning65: 'jubilados',
  turning65During: 'proximos-jubilados',
}

const EXPORT_FILENAME: Record<string, string> = {
  coverage: 'Asegurados por Cobertura',
  campus: 'Titulares por Campus',
  turning65: 'Jubilados',
  turning65During: 'Proximos Jubilados',
  cargos: 'Cargos por Cobertura',
}

const MODAL_TITLES: Record<DetailType, string> = {
  coverage: 'Asegurados por Cobertura',
  campus: 'Titulares por Campus',
  turning65: 'Jubilados',
  turning65During: 'Próximos Jubilados',
  cargos: 'Cargos por Cobertura',
}

@Component({
  selector: 'dashboard-data',
  templateUrl: './dashboard-data.html',
  providers: [RequestInsuredService, EntityService, RequestService, PolicyService],
  standalone: true,
  imports: [PanelComponent, CoreModule, NgxEchartsDirective, DashboardDetailModalComponent],
})
export class DashboardDataPage {
  private _requestService: RequestInsuredService = inject(RequestInsuredService)
  private _entityService: EntityService = inject(EntityService)
  private _requestExportService: RequestService = inject(RequestService)
  private _policyService: PolicyService = inject(PolicyService)
  private _modalService: NgbModal = inject(NgbModal)

  private destroyRef = inject(DestroyRef)

  protected period = new Date().toISOString().slice(0, 7)
  protected insuredMonth = new Date().toISOString().slice(0, 7)
  protected loading = false
  protected loadingInsured = false
  protected data: any = null
  protected entities: any[] = []
  protected coverages: any[] = []
  protected selectedEntityId: string | null = null
  protected selectedCoverageId: string | null = null

  protected chartTypeOptions: EChartsCoreOption = {}
  protected chartStateOptions: EChartsCoreOption = {}

  ngOnInit(): void {
    this.loadEntities()
    this.loadCoverages()
    this.load()
    this.loadInsured()
  }

  loadEntities() {
    this._entityService.getAll().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => { this.entities = res },
    })
  }

  loadCoverages() {
    this._policyService.getCoverages().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => { this.coverages = res },
    })
  }

  load() {
    if (!this.period) return
    this.loading = true
    this._requestService.getDashboardData(this.period, this.selectedEntityId ?? undefined).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.data = { ...this.data, requests: res.requests, period: res.period }
        this.buildCharts()
        this.loading = false
      },
      error: () => { this.loading = false },
    })
  }

  loadInsured() {
    if (!this.insuredMonth) return
    this.loadingInsured = true
    this._requestService.getInsuredSummary(this.insuredMonth, this.selectedEntityId ?? undefined, this.selectedCoverageId ?? undefined).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.data = { ...this.data, insured: res }
        this.loadingInsured = false
      },
      error: () => { this.loadingInsured = false },
    })
  }

  private buildCharts() {
    if (!this.data?.requests) return
    this.chartTypeOptions = this.buildTypeChart()
    this.chartStateOptions = this.buildStateChart()
  }

  private buildTypeChart() {
    const r = this.data.requests
    return this.makeDonut(
      'Solicitudes por Tipo',
      [
        { name: 'Incorporación', value: r.byType.incorporation },
        { name: 'Exclusión', value: r.byType.exclusion },
        { name: 'Transferencias', value: r.byType.transferIn + r.byType.transferOut },
        { name: 'Modif. Capital', value: r.byType.modifyCapital },
      ],
      ['#2d7ff9', '#ff3b30', '#5ac8fa', '#af52de'],
    )
  }

  private buildStateChart() {
    const r = this.data.requests
    return this.makeDonut(
      'Solicitudes por Estado',
      [
        { name: 'En Revisión', value: r.byState.inReview },
        { name: 'Aprobados', value: r.byState.approved },
        { name: 'Rechazados', value: r.byState.rejected },
        { name: 'Cancelados', value: r.byState.cancelled },
      ],
      ['#6c757d', '#20c997', '#ff3b30', '#ffc107'],
    )
  }

  private makeDonut(title: string, data: { name: string; value: number }[], colors: string[]): EChartsCoreOption {
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { orient: 'vertical', right: 20, top: 'center', textStyle: { fontSize: 12 } },
      series: [{
        type: 'pie',
        radius: ['45%', '70%'],
        center: ['40%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 13, fontWeight: 'bold' } },
        data,
      }],
      color: colors,
    }
  }

  openDetail(type: DetailType) {
    const modalRef = this._modalService.open(DashboardDetailModalComponent, { size: 'xl', scrollable: true, animation: true })
    modalRef.componentInstance.data = this.data?.insured
    modalRef.componentInstance.detailType = type
    modalRef.componentInstance.detailTitle = MODAL_TITLES[type]
    modalRef.componentInstance.onExport = (t: string) => this.exportExcel(t)
  }

  private downloadBlob(res: Blob, filename: string) {
    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    window.URL.revokeObjectURL(url)
  }

  exportExcel(type?: string) {
    Swal.fire({
      text: 'Generando Archivo...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading() },
    })
    const month = this.insuredMonth || this.period
    const endpoint = type ? 'report/excel/insured-modal-export' : 'report/excel/insured-by-policy'
    const params: any = type
      ? { type: EXPORT_API_TYPE[type] || type, period: month, entityId: this.selectedEntityId }
      : { period: month, entityId: this.selectedEntityId }
    const filename = type
      ? `${EXPORT_FILENAME[type] || type}_${month}.xlsx`
      : `AseguradosPorPoliza_${month}.xlsx`
    this._requestExportService.generateExcel(endpoint, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: Blob) => {
      this.downloadBlob(res, filename)
      Swal.close()
    })
  }
}
